import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import FecthingPage from "../../CommonComponents/FecthingPage";
import { ownerAccountsHistoricBalanceReport } from "../../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import dayjs from "dayjs";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import { toNumber } from "lodash";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * IncomeOnIdleFunds - A component that displays income on idle funds amount trends over time
 * using a dual-line graph. Shows daily actual income and potential income on idle funds amounts with totals. Supports filtering
 * by time period and amount unit.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.flow - Display flow: 'homeDashboard' or other
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @returns {JSX.Element} Rendered debit/credit amount graph component
 */
const IncomeOnIdleFunds = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  /**
   * Default fallback data for line graph - used as initial state before API fetch.
   * @type {Object}
   * @property {number[]} defaultItemsSizeForXAxis - Array of indices for x-axis positions
   * @property {number[]} dataFirst -  missed potential line data
   * @property {number[]} dataSecond - actual income line data
   * @property {string[]} xAxisData - Date labels for x-axis
   * @property {number} totalMissedPotential - Total missed potential amount
   * @property {number} totalActualIncome - Total actual income amount
   */
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [35, 55, 45, 60, 45, 40, 60],
    dataSecond: [0, 55, 0, 0, 0, 0, 0],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    totalActualIncome: 50,
    totalMissedPotential: 20,
  };

  // Graph data state - initialized with default dummy data
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  /**
   * IMPORTANT: Below state `isInitialAutoFetching`:
   * Tracks the auto-fetch lifecycle when the component mounts with autoFetch enabled.
   *
   * Purpose:
   * 1. Eliminates loader flickering between the time owner accounts finish fetching
   *    in GraphHeader and the subsequent dispatch of OdInterestSavedThunk.
   * 2. Prevents the default/dummy graph from flashing on initial mount.
   * 3. Initialized to `autoFetch` so that manual-fetch mode (autoFetch = false)
   *    displays the "Fetch" action button instead of getting stuck in a loading state.
   */
  const [isInitialAutoFetching, setIsInitialAutoFetching] = useState(autoFetch);

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  // Amount unit filter state (e.g., "", "thousand", "lakh", "crore")
  const [selectedAmount, setSelectedAmount] = useState("");

  // Selected favorite account for filtering
  const [selectedAccount, setSelectedAccount] = useState("");

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  // Raw report data from API (stored for re-calculation when unit changes)
  const [rawReportData, setRawReportData] = useState(null);
  const toastShownRef = useRef(false);
  const services = useSelector((state) => state.refreshToken.services);
  const ownerAccLoading = useSelector(
    (state) => state.ownerAllVerification.ownerViewAllListAccLoading,
  );

  const globalStatisticsService = services?.includes(
    globalText?.VIEW_STATISTICS,
  ); // Global service for statistics "VStatistics".
  const homeDashboard = toCamelCase(globalText?.HOME_DASHBOARD);

  const dispatch = useDispatch();

  const loginData = useSelector((state) => state.refreshToken.data);

  // Select loading and error states from Redux
  const { getOwnerAccStatiticsError, getOwnerAccStatiticsLoading } =
    useSelector((state) => state.ownerAllVerification);

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /**
   * Transforms API report data into arrays suitable for dual-line graph rendering.
   * Handles amount formatting based on selected unit and extracts date labels.
   */
  const transformReportData = (reports, selectedAmount) => {
    return reports.reduce(
      (acc, val, index) => {
        // X-axis position index
        acc.defaultGraphDataItemsForXaxis.push(index);

        // Primary data: missed potential (formatted if unit selected)
        acc.graphDataFirst.push(
          selectedAmount
            ? toNumber(
                formatTotalBalance(val.daily_interest, selectedAmount).amount,
              ) || GlobalConfiguration?.lengthCheckZero
            : val.daily_interest || GlobalConfiguration?.lengthCheckZero,
        );

        // Secondary data: actual income (formatted if unit selected)
        acc.graphDataSecond.push(
          selectedAmount && val.actual_income
            ? toNumber(
                formatTotalBalance(val.actual_income, selectedAmount).amount,
              ) || GlobalConfiguration?.lengthCheckZero
            : selectedAmount // NOTE: remove this condition once backend send actual_income in payload
              ? GlobalConfiguration?.lengthCheckZero
              : val.actual_income || GlobalConfiguration?.lengthCheckZero,
        );

        // Extract date label - prefers 'day' property, falls back to 'date'
        if (Object.hasOwn(val, globalText?.DAY.toLowerCase())) {
          acc.xAxisLabels.push(val.day);
        } else {
          acc.xAxisLabels.push(val.date);
        }

        // Calculate totals
        acc.totalMissedPotential +=
          val.daily_interest || GlobalConfiguration?.lengthCheckZero;
        acc.totalActualIncome +=
          val.actual_income || GlobalConfiguration?.lengthCheckZero;

        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
        totalMissedPotential: GlobalConfiguration?.lengthCheckZero,
        totalActualIncome: GlobalConfiguration?.lengthCheckZero,
      },
    );
  };

  /**
   * Processes API response and updates component state with credit/debit data.
   * On success: transforms data, shows success toast (once), updates graph data.
   * On failure: resets to default data, conditionally enables fetch page, sets error.
   */
  const handleCalculation = useCallback(
    (response) => {
      const reportData = response?.payload?.owner_account_details;

      if (response?.payload?.status && reportData) {
        // Store raw data for re-calculation when amount unit changes
        setRawReportData(reportData);

        // Transform report data for graph
        const {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          graphDataSecond,
          xAxisLabels,
          totalActualIncome,
          totalMissedPotential,
        } = transformReportData(
          reportData.account_balances_report || [],
          selectedAmount,
        );

        // Update graph data with transformed arrays and summary totals
        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          dataSecond: graphDataSecond,
          xAxisData: xAxisLabels,
          totalMissedPotential: totalMissedPotential,
          totalActualIncome: totalActualIncome,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: globalText?.FETCHED_SUCCESSFULLY, //"Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        // Enable fetch page unless it's a "not found" error (no data exists)
        setFetchPageEnabled(
          // response?.payload?.status !== errorcode?.errorNotFound,
          // NOTE: Will revert this to error code 404 as of now API is sending in-appropriate error if no data is present.
          response?.payload?.status !== errorcode?.unprocessableContent,
        );
        toastShownRef.current = false;
        setIsError(true);
      }
    },
    [selectedDays, entityId],
  );

  /**
   * Fetches balance historic data from API and updates component state.
   * Requires valid entity ID.
   */
  const fetchData = async () => {
    // Guard: no account selected
    if (!selectedAccount) return;

    // Guard: no entity ID
    if (!entityId) return;
    setIsError(false);

    // calculation of start date and end date
    let start_date = "";
    let end_date = "";
    if (selectedDays === globalText?.LAST_7_DAYS.toLowerCase()) {
      start_date = dayjs()
        .subtract(
          GlobalConfiguration?.lengthCheckSeven,
          globalText?.DAY.toLowerCase(),
        )
        .format("YYYY-MM-DD");
      end_date = dayjs()
        .subtract(
          GlobalConfiguration?.lengthCheckOne,
          globalText?.DAY.toLowerCase(),
        )
        .format("YYYY-MM-DD");
    } else if (selectedDays === globalText?.LAST_28_DAYS.toLocaleLowerCase()) {
      start_date = dayjs()
        .subtract(28, globalText?.DAY.toLowerCase())
        .format("YYYY-MM-DD");
      end_date = dayjs()
        .subtract(
          GlobalConfiguration?.lengthCheckOne,
          globalText?.DAY.toLowerCase(),
        )
        .format("YYYY-MM-DD");
    }
    setIsInitialAutoFetching(true);

    const response = await dispatch(
      ownerAccountsHistoricBalanceReport({
        id: selectedAccount.value,
        start_date: start_date,
        end_date: end_date,
        interest_rate: SettingsStatisticsConfiguration?.interestRate,
        investment_percent: SettingsStatisticsConfiguration?.investmentPercent,
      }),
    );
    setIsInitialAutoFetching(false);
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /**
   * Handler for manual "Fetch Data" button click.
   * Initiates fetch by enabling loading and disabling fetch page.
   */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Re-runs when autoFetch, fetchPageEnabled, or selectedDays changes.
   */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedAccount]);

  /**
   * Effect: Recalculate graph data when only the amount unit changes.
   * Uses cached rawReportData to avoid re-fetching from API.
   * Re-runs when selectedAmount or rawReportData changes.
   */
  useEffect(() => {
    if (rawReportData) {
      const {
        defaultGraphDataItemsForXaxis,
        graphDataFirst,
        graphDataSecond,
        xAxisLabels,
        totalActualIncome,
        totalMissedPotential,
      } = transformReportData(
        rawReportData.account_balances_report || [],
        selectedAmount,
      );

      setGraphData({
        defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
        dataFirst: graphDataFirst,
        dataSecond: graphDataSecond,
        xAxisData: xAxisLabels,
        totalMissedPotential: totalMissedPotential,
        totalActualIncome: totalActualIncome,
      });
    } else {
      setGraphData(DEFAULT_GRAPH_DATA);
    }
  }, [selectedAmount, selectedAccount, rawReportData]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === homeDashboard ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      {/* Header with title, period filter, amount unit filter, and add-to-home toggle */}
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: `${globalText?.AMOUNT.toLowerCase()}s`,
            setterState: setSelectedAmount,
          },
          {
            type: toCamelCase(globalText?.FAVOURITE_ACC),
            setterState: setSelectedAccount,
          },
        ]}
        flow={flow}
        autoFetch={autoFetch}
        titleText={globalText?.INCOME_ON_IDLE_FUNDS}
        hideTitle={flow === homeDashboard ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col relative">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={
            getOwnerAccStatiticsLoading ||
            fetchPageLoading ||
            isInitialAutoFetching ||
            (ownerAccLoading && autoFetch)
          }
          isError={isError}
          errorData={
            getOwnerAccStatiticsError
              ? // getOwnerAccStatiticsError === errorcode?.errorNotFound
                { status: errorcode?.errorNotFound }
              : null
          }
          handleFunction={handleFetchClick}
        />

        {/* Line Graph Container with horizontal scrolling for many data points */}
        <div className="w-width-full pb-padding-sm relative overflow-x-auto ">
          {/* Dynamic minimum width based on data points count for proper spacing */}
          <div
            style={{
              minWidth: `${Math.max((graphData.xAxisData?.length || GlobalConfiguration?.lengthCheckZero) * SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint)}px`,
            }}
          >
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--queued)"}
              graphDataSecondColor={"var(--settled)"}
              enableDynamicZeroColoring={true}
              zeroValueColor={"var(--failed)"}
              nonZeroValueColor={"var(--settled)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
              amountFormatForLegend={selectedAmount}
              period={selectedDays}
            />
          </div>
        </div>

        {/* Footer with missed income & actual income totals */}
        <div className="w-width-full bg-primary-nav rounded-b-border-radius-md">
          <div className="flex Qsmd:flex-row flex-col gap-gap-sm py-padding-sm px-padding-md">
            {/* Total missed income Card */}
            <div className="flex gap-gap-md items-center Qsmd:flex-1">
              <div className="text-text-secondary text-font-xxs">
                Total missed income :
              </div>
              <div className="text-queued text-font-xs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.totalMissedPotential,
                      selectedAmount === ""
                        ? globalText?.AMOUNT.toLowerCase() //"amount"
                        : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>

            {/* Total actual income card - bordered separator on desktop */}
            <div className="flex Qsmd:flex-1 gap-gap-md items-center Qsmd:border-l-border Qsmd:border-t-border-base Qsmd:pl-padding-md border-t-border Qsmd:border-l-primary-bg border-t-primary-bg Qsmd:pt-0 pt-padding-sm ">
              <div className="text-text-secondary text-font-xxs">
                Total actual income :
              </div>
              <div
                className={`${graphData?.totalActualIncome > GlobalConfiguration?.lengthCheckZero ? `text-settled` : `text-failed`} text-font-xs font-semibold`}
              >
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(
                      graphData.totalActualIncome,
                      selectedAmount === ""
                        ? globalText?.AMOUNT.toLowerCase() //"amount"
                        : selectedAmount,
                    ).amount +
                    " " +
                    selectedAmount}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeOnIdleFunds;
